/**
 * `<CoverageSummarySection/>` — lead-side coverage gaps summary for the persistent right panel (PR 3, #198).
 *
 * Renders a one-line status (✓ Fully covered / M days short)
 * with a "View details →" deep link to the Roster tab where the full
 * `<CoverageGapsPanel/>` + `<CoverageGapsHeatmap/>` live.
 *
 * **Why "days short" leads, not "gaps".** Gap count double-counts when
 * multiple shifts on the same day have shortfalls — a lead acts on the
 * day, not on the per-shift gap. Wireframe doc lines 642-643 spec the
 * action-relevant framing. Gap count remains as the secondary detail.
 *
 * **The badge counts seats** (flows finding 29, 1 Oct 2026): empty seats
 * against headcount (wards, calls, pooled floors), so it reads "N seats
 * short", not "N gaps". Since #2027 item 5 the roster page's Coverage chip
 * reads this same count (it read coverage-review cells before, and the two
 * used to read as "197 short" vs "265 gaps").
 *
 * **Read from `shortfalls_by_day`, not `coverage_gaps`** (flows finding 43,
 * 1 Oct 2026). `coverage_gaps` carries the ward + call rows only — the
 * pooled group floors ("one registrar across group A, PM") never join it,
 * and it does carry the composition-rule (attribute carrier) rows, which
 * are not empty seats. So the badge silently dropped the floors: on the
 * truth-pass team a re-solve moved the floors 30 → 36 and the grid header
 * −296 → −302 while the badge held at 266 (every ward/call gap sat on the
 * same three unfillable days), and "5 days short" ignored the eight days
 * short only on a floor. `shortfalls_by_day` is the roster read's own
 * per-day seat count — wards, calls and floors, headcount only — the same
 * map the grid's day headers and its "N seats short" total sum, so the badge
 * and the grid header now read one number.
 *
 * Always-on per #193 (coverage is core scheduling, not opt-in).
 *
 * **Mount conditions.**
 *   - Cycle context resolved from URL (`useParams.cycleId`).
 *   - Cycle is in `published` state — pre-publish there's no roster
 *     to read coverage gaps from; section silently hides.
 *
 * **Data flow.** Reuses `useCycleRoster` so this section's mount
 * + the Roster tab's mount share a single GET (per `useCycleRoster`'s
 * cache-key contract, issue #127). No duplicate request.
 *
 * **Self-hides** on fetch error or when not published.
 *
 * **Focal-component discipline.** The badge (gap count + tone) is the
 * section focal; the header + deep-link are calm supporting context.
 */
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import { useCycle } from "../api/useCycle";
import { useCycleRoster } from "../api/useCycleRoster";
import { seatsShortOver } from "../lib/seatsShort";
import { Badge, Skeleton } from "./ui";

/**
 * The section's reads + derived counts, shared with the panel handle's
 * badge (#1920, `lib/persistentPanelBadge.ts`) so both read one cache and
 * one definition of "seats short". `showsCount` is true exactly when the
 * section renders its "N days short" line.
 */
export function useCoverageSummary(cycleId: string | undefined) {
  const cycleQuery = useCycle(cycleId);

  const cycleState = cycleQuery.data?.state;
  const isPublished = cycleState === "published";

  const rosterQuery = useCycleRoster(cycleId ?? "", { enabled: isPublished });

  const { gapCount, daysAffected } = useMemo(() => {
    // Flows finding 43 — per-day empty seats incl. pooled floors (see above).
    // #2027 item 5 — the one "short" definition (`lib/seatsShort.ts`).
    const byDay = rosterQuery.data?.shortfalls_by_day ?? {};
    const short = seatsShortOver(byDay, Object.keys(byDay));
    return { gapCount: short.seats, daysAffected: short.shortDays };
  }, [rosterQuery.data]);

  const showsCount =
    Boolean(cycleId) &&
    isPublished &&
    !cycleQuery.isError &&
    !rosterQuery.isError &&
    !rosterQuery.isLoading &&
    gapCount > 0;

  return { cycleQuery, cycleState, isPublished, rosterQuery, gapCount, daysAffected, showsCount };
}

export function CoverageSummarySection() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const { cycleQuery, cycleState, isPublished, rosterQuery, gapCount, daysAffected } =
    useCoverageSummary(cycleId);

  if (!cycleId) return null;
  if (cycleQuery.isError || rosterQuery.isError) return null;
  if (cycleQuery.isLoading || !cycleState) return null;
  if (!isPublished) return null;

  return (
    <section
      data-testid="coverage-summary-section"
      className="flex flex-col gap-2 px-1 py-2"
      aria-label="Coverage summary"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-fg">Coverage</div>
        <Link
          to={`/cycles/${cycleId}/roster`}
          className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
          data-testid="coverage-summary-deep-link"
        >
          View details
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </header>

      {rosterQuery.isLoading ? (
        <Skeleton className="h-8" data-testid="coverage-summary-loading" />
      ) : gapCount === 0 ? (
        <div className="flex items-center gap-2" data-testid="coverage-summary-clear">
          <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
          <span className="text-sm font-medium text-fg">Fully covered</span>
        </div>
      ) : (
        <div
          className="flex flex-col gap-1"
          data-testid="coverage-summary-gaps"
          data-gap-count={gapCount}
          data-days-affected={daysAffected}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
            <span className="text-sm font-medium text-fg">
              {daysAffected} {daysAffected === 1 ? "day" : "days"} short
            </span>
            <Badge tone="warning" data-testid="coverage-summary-seats">
              {gapCount} {gapCount === 1 ? "seat" : "seats"} short
            </Badge>
          </div>
        </div>
      )}
    </section>
  );
}
